<script setup lang="ts">
import type { FileManagerMount } from '@/components/document/sub-components/file-manager';
import type { ProjectSyncTarget } from '@/composeables/use-project-mounts';
import {
  PhCloudArrowUp,
  PhCloudCheck,
  PhSpinnerGap,
} from '@phosphor-icons/vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useOneDriveSync } from './useOneDriveSync';

const { t } = useI18n();

const props = defineProps<{
  target: ProjectSyncTarget | undefined;
  mounts: FileManagerMount[];
}>();

const { state, folderWebUrl, sync } = useOneDriveSync(
  () => props.target,
  () => props.mounts,
);

const isBusy = computed(
  () => state.value === 'checking' || state.value === 'pending',
);

const label = computed(() => {
  if (state.value === 'checking')
    return t('documentPane.oneDriveSync.checkingLabel');
  if (state.value === 'pending')
    return t('documentPane.oneDriveSync.pendingLabel');
  return t('documentPane.oneDriveSync.buttonLabel');
});
</script>

<template>
  <div v-if="state !== 'hidden'" class="onedrive-sync">
    <a
      v-if="state === 'synced'"
      class="sync-button is-synced"
      :href="folderWebUrl"
      target="_blank"
      rel="noopener noreferrer"
      :title="t('documentPane.oneDriveSync.openFolderLabel')"
    >
      <PhCloudCheck :size="16" weight="fill" />
      <span class="sync-button-label">{{
        t('documentPane.oneDriveSync.syncedLabel')
      }}</span>
    </a>
    <button
      v-else
      type="button"
      class="sync-button"
      :disabled="isBusy"
      :aria-busy="isBusy"
      @click="sync"
    >
      <PhSpinnerGap v-if="isBusy" :size="16" class="is-spinning" />
      <PhCloudArrowUp v-else :size="16" />
      <span class="sync-button-label">{{ label }}</span>
    </button>
  </div>
</template>

<style scoped>
.onedrive-sync {
  display: flex;
  justify-content: flex-end;
  height: 1.875rem;
  margin-bottom: 0.5rem;
}

.sync-button {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  height: 100%;
  padding: 0 0.875rem;
  border: 1px solid var(--primary-color);
  border-radius: 999px;
  background-color: var(--primary-color);
  color: #fff;
  font-size: 0.875rem;
  font-family: inherit;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
}

.sync-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.sync-button.is-synced {
  background-color: #f0f0f2;
  border-color: #f0f0f2;
  color: #4b5563;
}

.sync-button-label {
  display: flex;
  align-items: center;
  line-height: 1;
}

.is-spinning {
  animation: onedrive-sync-spin 1s linear infinite;
}

@keyframes onedrive-sync-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
